import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast, Toaster } from "sonner";
import { Loader2, Copy, Sparkles, CheckCircle2, KeyRound } from "lucide-react";
import JsonTree from "@/components/JsonTree";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

export default function PocAI() {
  const [form, setForm] = useState({
    topic: "Algebra Basics",
    level: "Beginner",
    audience: "Grade 8 students",
    num_chapters: 3,
    extra_instructions: "Indian curriculum context",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(`${API}/ai/generate-course`, {
        ...form,
        num_chapters: Number(form.num_chapters),
      });
      setResult(res.data);
      toast.success("Course generated", {
        description: `${res.data.course.chapters.length} chapters in ${res.data.generation_ms} ms`,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Unknown error generating course";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Generation failed", { description: String(msg).slice(0, 200) });
    } finally {
      setLoading(false);
    }
  };

  const copyJson = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(JSON.stringify(result.course, null, 2));
    toast.success("Copied JSON to clipboard");
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="poc-ai-page">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="h-9 w-9 rounded-md bg-amber-500 text-stone-900 flex items-center justify-center font-bold tracking-tight"
              aria-hidden
            >
              <KeyRound className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="text-base font-semibold tracking-tight">
                KKS — AI Course Generator
              </div>
              <div className="text-xs text-stone-500">Phase 0 · POC</div>
            </div>
          </div>
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 text-amber-900"
            data-testid="phase-badge"
          >
            Knowledge is Key to Success
          </Badge>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10 grid gap-8 lg:grid-cols-[420px_1fr]">
        {/* Form */}
        <Card className="border-stone-200" data-testid="generate-form-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-600" />
              Generate a course
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={onGenerate}>
              <div className="space-y-2">
                <Label htmlFor="topic">Topic</Label>
                <Input
                  id="topic"
                  data-testid="input-topic"
                  value={form.topic}
                  onChange={(e) => update("topic", e.target.value)}
                  placeholder="e.g. Photosynthesis"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="level">Level</Label>
                  <Select
                    value={form.level}
                    onValueChange={(v) => update("level", v)}
                  >
                    <SelectTrigger id="level" data-testid="select-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((l) => (
                        <SelectItem
                          key={l}
                          value={l}
                          data-testid={`level-option-${l.toLowerCase()}`}
                        >
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="num_chapters">Chapters</Label>
                  <Input
                    id="num_chapters"
                    data-testid="input-num-chapters"
                    type="number"
                    min={1}
                    max={10}
                    value={form.num_chapters}
                    onChange={(e) => update("num_chapters", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="audience">Audience</Label>
                <Input
                  id="audience"
                  data-testid="input-audience"
                  value={form.audience}
                  onChange={(e) => update("audience", e.target.value)}
                  placeholder="e.g. Grade 8 students"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="extra">Extra instructions</Label>
                <Textarea
                  id="extra"
                  data-testid="input-extra"
                  value={form.extra_instructions}
                  onChange={(e) =>
                    update("extra_instructions", e.target.value)
                  }
                  placeholder="Any specific curriculum, examples, or style"
                  rows={3}
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                data-testid="generate-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate course
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Output */}
        <section className="space-y-4" data-testid="output-section">
          {!result && !error && !loading && (
            <Card className="border-dashed border-stone-300 bg-white">
              <CardContent className="py-16 text-center text-stone-500">
                <Sparkles className="mx-auto h-6 w-6 text-amber-500 mb-3" />
                <p className="text-sm">
                  Fill the form and hit <span className="font-medium">Generate</span>{" "}
                  to see the structured course JSON here.
                </p>
              </CardContent>
            </Card>
          )}

          {loading && (
            <Card className="border-stone-200 bg-white">
              <CardContent className="py-16 text-center text-stone-600">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-stone-700 mb-3" />
                <p className="text-sm">
                  Generating course with the LLM… typically 15–45s.
                </p>
              </CardContent>
            </Card>
          )}

          {error && (
            <Card
              className="border-red-200 bg-red-50"
              data-testid="error-card"
            >
              <CardHeader>
                <CardTitle className="text-base text-red-800">
                  Generation failed
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-red-800 whitespace-pre-wrap break-words">
                {error}
              </CardContent>
            </Card>
          )}

          {result && (
            <>
              <Card className="border-stone-200 bg-white" data-testid="meta-card">
                <CardContent className="py-5 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <div>
                      <div className="text-sm font-semibold">
                        {result.course.course_title}
                      </div>
                      <div className="text-xs text-stone-500">
                        {result.course.chapters.length} chapters ·{" "}
                        {result.course.level}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-stone-600">
                    <Badge variant="secondary" data-testid="model-badge">
                      model: {result.model}
                    </Badge>
                    <Badge variant="secondary" data-testid="latency-badge">
                      {result.generation_ms} ms
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyJson}
                      data-testid="copy-json-button"
                    >
                      <Copy className="mr-2 h-3.5 w-3.5" /> Copy JSON
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200 bg-white" data-testid="json-card">
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-stone-700">
                    Course JSON
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className="max-h-[70vh] overflow-auto rounded-md border border-stone-200 bg-stone-50 p-3 font-mono text-xs"
                    data-testid="json-tree"
                  >
                    <JsonTree data={result.course} defaultOpen />
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </section>
      </main>

      <footer className="mx-auto max-w-6xl px-6 py-8 text-xs text-stone-500">
        POC only · No auth · Uses Emergent Universal LLM key
      </footer>
    </div>
  );
}
